/* eslint-disable no-magic-numbers */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LazyLoadEvent } from 'primeng/api';
import {
  ServiceLineRule,
  ServiceLineRuleQuery,
  ServiceLineRulesReferences,
  RoutingUser,
} from '@shared/models/service-line-rule.model';
import { RulesBaseService } from '@core/services/rules/rules-base.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ProductName } from '@core/constants';

const USER_WARNINGS = {
  isInactive: 'The user was not found in the active directory.\n The rule will not be applied.',
  notOperational: {
    [ProductName.PAC_MA]: 'Not a PAC Operations user.\n The rule will not be applied.',
    [ProductName.PAC_COM]: 'Not a PAC Commercial Operations user.\n The rule will not be applied.',
    [ProductName.HH]: 'The user not found in the active directory. The rule will not be applied.',
  },
};

export const propToParentReferenceMap: Record<string, string> = {
  userId: 'user',
  requestType: 'requestTypes',
  authorizationType: 'authorizationTypes',
  postalState: 'postalStates',
  npi: 'npIs',
  orderSubtype: 'orderSubtypes',
  hcpcCode: 'hcpcCodes',
  tenant: 'tenants',
  providerDSERuleGroup: 'providerDSERuleGroups',
};

@Injectable({
  providedIn: 'root',
})
export class ServiceLinesRulesService extends RulesBaseService {
  uniqueKey = 'serviceLineRuleId';

  private get apiBaseUrl(): string {
    if (this.API_URL.endsWith('/')) {
      return this.API_URL;
    }

    return `${this.API_URL}/`;
  }

  query(rawQuery: LazyLoadEvent): Observable<any> {
    const query = this.buildQuery(rawQuery);

    return this.http.post<ServiceLineRuleQuery>(
      `${this.apiBaseUrl}${this.productName.value}/serviceLineRules/$query`,
      `"${query}"`,
      this.options
    );
  }

  add(payload: ServiceLineRule): Observable<ServiceLineRule> {
    return this.http.post<ServiceLineRule>(`${this.apiBaseUrl}${this.productName.value}/serviceLineRules`, payload);
  }

  update(id: string, payload: ServiceLineRule): Observable<ServiceLineRule> {
    return this.http.put<ServiceLineRule>(
      `${this.apiBaseUrl}${this.productName.value}/serviceLineRules/${id}`,
      payload
    );
  }

  delete(id: string): Observable<null> {
    return this.http.delete<null>(`${this.apiBaseUrl}${this.productName.value}/serviceLineRules/${id}`);
  }

  getReferences(): Observable<ServiceLineRulesReferences> {
    return this.http.get<ServiceLineRulesReferences>(
      `${this.apiBaseUrl}${this.productName.value}/serviceLineRules/references`,
      this.options
    );
  }

  getUsers(): Observable<RoutingUser[]> {
    return this.http.get<RoutingUser[]>(`${this.apiBaseUrl}${this.productName.value}/users/operations`, this.options);
  }

  getUserWarnings(rule: ServiceLineRule) {
    const {
      user: { status },
    } = rule;

    return {
      error: status !== 'Active',
      message:
        status === 'Inactive'
          ? USER_WARNINGS.isInactive
          : (USER_WARNINGS.notOperational as any)[this.productName.value as string],
    };
  }

  // eslint-disable-next-line consistent-return
  createForm() {
    switch (this.productName.value) {
      case ProductName.PAC_MA:
      case ProductName.PAC_COM:
        return new FormGroup({
          authorizationTypes: new FormControl([]),
          dailyLimit: new FormControl(null, [Validators.min(0), Validators.max(99999)]),
          isActive: new FormControl(true),
          npIs: new FormControl([]),
          postalStates: new FormControl([]),
          requestTypes: new FormControl([]),
          rowVersion: new FormControl(null),
          secondLevelReview: new FormControl(null),
          serviceLineRuleId: new FormControl(),
          // Used for identification newly created rules in case when rule creation was faled with error 'General-136'
          // (Service line rule update failed due to not found NPIs per client.)
          temporaryUniqueKey: new FormControl(crypto.randomUUID()),
          specialty: new FormControl(null),
          tenants: new FormControl([]),
          totalLimit: new FormControl(null, [Validators.min(0), Validators.max(99999), Validators.required]),
          user: new FormControl(null, [Validators.required]),
          userWarnings: new FormControl({}),
        });
      case ProductName.HH:
        return new FormGroup({
          dailyLimit: new FormControl(null, [Validators.min(0), Validators.max(99999)]),
          hcpcCodes: new FormControl([]),
          isActive: new FormControl(true),
          orderSubtypes: new FormControl([]),
          providerDSERuleGroups: new FormControl([]),
          postalStates: new FormControl([]),
          requestTypes: new FormControl([]),
          rowVersion: new FormControl(null),
          secondLevelReview: new FormControl(null),
          serviceLineRuleId: new FormControl(null),
          specialty: new FormControl(null),
          tenants: new FormControl([]),
          totalLimit: new FormControl(null, [Validators.min(0), Validators.max(99999), Validators.required]),
          user: new FormControl(null, [Validators.required]),
          userWarnings: new FormControl({}),
        });
      default:
        return new FormGroup({});
    }
  }

  override rawQueryToOdataObject = (key: string, value: string[], matchMode: string) => {
    const ref = propToParentReferenceMap[key];

    switch (key) {
      case 'userId':
        if (matchMode === 'not') {
          return {
            [matchMode]: {
              [ref]: { [key]: { ['in']: value } },
            },
          };
        }

        return {
          [ref]: { [key]: { [matchMode]: value } },
        };
      case 'secondLevelReview':
      case 'specialty':
        return { [key]: { [matchMode]: value } };
      case 'requestType':
      case 'npi':
      case 'postalState':
      case 'providerDSERuleGroup':
      case 'orderSubtype':
      case 'hcpcCode':
      case 'tenant':
      case 'authorizationType':
        return this.authTypeParse(value, matchMode, ref);
      case 'isActive':
        return {
          [key]: value,
        };
      default:
        return {};
    }
  };

  private authTypeParse(value: string[], matchMode: string, ref: string) {
    const index = value.indexOf('null');
    const nullExists = index > -1;

    if (nullExists) {
      value.splice(index, 1);
    }

    const collection = {
      [ref]: {
        any: { '': { [matchMode]: value } },
      },
    };

    if (nullExists) {
      const or = value.length > 0;

      if (or) {
        return {
          or: [{ not: { [ref]: { any: [] } } }, collection],
        };
      } else {
        return { not: { [ref]: { any: [] } } };
      }
    }

    return collection;
  }
}
